import { styled } from "goober";
import { useSessionTenantOrigin } from "../../../hooks/useSessionTenantOrigin";
import { Box } from "../../Box";
import { Checkbox } from "../../Checkbox";
import { PressButton } from "../../PressButton";
import { Text } from "../../Text";
import { CopyableField } from "./CopyableField";
import { Icon } from "../../Icon";
import {
  type ScimToken,
  ScimTokenBadge,
  ScimTokenStatus,
} from "./ScimTokenStatus";
import { Field } from "./shared";
import { scimBaseUrl } from "./urls";

export type SyncMode = "off" | "jit" | "jit_users_and_groups" | "scim";

export type SyncModeState = {
  syncMode: SyncMode;
};

const SYNC_MODE_OPTIONS: {
  value: SyncMode;
  title: string;
  qualifier?: string;
  description: string;
}[] = [
  {
    value: "off",
    title: "Off",
    description: "Invite people and manage their groups in 3B.",
  },
  {
    value: "jit",
    title: "JIT",
    qualifier: "users only",
    description:
      "Create an account the first time someone signs in. Manage their groups in 3B.",
  },
  {
    value: "jit_users_and_groups",
    title: "JIT",
    qualifier: "users and groups",
    description:
      "Create an account the first time someone signs in, and update their groups from your IdP each time they sign in.",
  },
  {
    value: "scim",
    title: "SCIM",
    description:
      "Your IdP keeps people and groups in sync. People can’t sign in until your IdP adds them.",
  },
];

const OptionsContainer = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  overflow: hidden;
`;

const OptionRow = styled(PressButton)`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  width: 100%;
  text-align: left;
  user-select: none;
  &:not(:last-child) {
    border-bottom: 0.5px solid var(--border-alpha);
  }
  &:hover {
    background: var(--alpha4);
  }
`;

const ScimSection = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  padding: 8px 8px 16px;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
`;
const OptionCheck = styled("div")`
  margin-left: auto;
  align-self: center;
  display: flex;
  &[data-selected="false"] {
    opacity: 0.2;
  }
`;

const Note = styled("div")`
  display: flex;
  gap: 6px;
  padding: 0 8px;
  color: var(--text-secondary);
`;

export function SyncModeFields({
  state,
  onChange,
  scimToken,
  scimUrlHint,
  modes,
}: {
  state: SyncModeState;
  onChange: (next: SyncModeState) => void;
  scimToken?: ScimToken;
  scimUrlHint: string;
  modes?: SyncMode[];
}) {
  const tenantOrigin = useSessionTenantOrigin();
  return (
    <Field>
      <Box horizontal={8} vertical={6}>
        <Text variant="text-small-md">Sync mode</Text>
      </Box>
      <OptionsContainer>
        {SYNC_MODE_OPTIONS.filter(
          (option) =>
            !modes ||
            modes.includes(option.value) ||
            option.value === state.syncMode
        ).map((option) => (
          <OptionRow
            key={option.value}
            data-no-pill
            onPress={() => onChange({ ...state, syncMode: option.value })}
          >
            <div>
              <Text variant="text-small-md">
                {option.title}
                {option.qualifier && (
                  <span style={{ color: "var(--text-secondary)" }}>
                    {" "}
                    <span aria-hidden>·</span> {option.qualifier}
                  </span>
                )}
              </Text>
              <Box top={2}>
                <Text variant="text-small" color="var(--text-secondary)">
                  {option.description}
                </Text>
              </Box>
            </div>
            <OptionCheck data-selected={state.syncMode === option.value}>
              <Checkbox checked={state.syncMode === option.value} />
            </OptionCheck>
          </OptionRow>
        ))}
      </OptionsContainer>
      {state.syncMode === "scim" && (
        <ScimSection>
          <CopyableField
            label="SCIM base URL"
            action={scimToken && <ScimTokenStatus {...scimToken} />}
            badge={
              scimToken && <ScimTokenBadge expiresAt={scimToken.expiresAt} />
            }
            hint={
              scimToken
                ? scimUrlHint
                : `${scimUrlHint}, with the token you’ll get after saving`
            }
            value={scimBaseUrl(tenantOrigin)}
          />
          {!scimToken && (
            <Note>
              <Icon name="info" size={12} />
              <Text variant="text-small" color="var(--text-secondary)">
                An IdP group with the same name as an existing group takes it
                over and keeps its access. Groups that still grant access can’t
                be deleted.
              </Text>
            </Note>
          )}
        </ScimSection>
      )}
    </Field>
  );
}
